<script setup lang="ts">
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import SearchIcon from "@/assets/search.svg";
    import { FormComponentType } from "@/components/forms/Form/convertFormData";
    import KAutocompleteOption from "@/components/forms/inputs/Autocomplete/KAutocompleteOption.vue";
    import { useValidation } from "@/components/forms/reusables/useValidation";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import KSuffix from "@/components/forms/subComponents/Suffix/KSuffix.vue";
    import { useStore } from "@/store";
    import { fetchData, profileOptions } from "@/tabs/Operation/utils.js";
    import { openDialog } from "@/utils";
    import { autoUpdate, computePosition, flip } from "@floating-ui/dom";
    import { cloneDeep, debounce } from "lodash";
    import isEqual from "lodash/isEqual";
    import { computed, inject, isProxy, onBeforeMount, onMounted, ref, toRefs, watch } from "vue";
    import VueSelect from "vue-select";
    import type { Option } from "./types";

    const store = useStore();

    interface Props {
        name: string;
        label?: string;
        /**
         * Options som ska visas från början. Det kan antingen vara en array (uppdateras reaktivt om den förändras,
         * t.ex. sätts async) eller async funktion som returnerar array med options. Funktionen anropas vid första
         * rendering
         */
        options?: Array<Option> | (() => Promise<Array<Option>>);
        /**
         * Funktion som anropas när text i input förändras. Ska returnera array med options
         */
        onSearch?: (props: {
            query?: string;
        }) => Promise<Array<{ value: string | number | boolean; label: string; [key: string]: unknown }>>;
        /**
         * Ska det gå att välja fler alternativ?
         */
        multiple?: boolean;
        /**
         * Endpoint Url. Använd den här om du inte skickat med onSearch. onSearch kommer inte anropas om url är satt
         */
        url?: string;
        /**
         * Används vid hämtning av data som har intern hjälpfunktion. Används i kombination med url. Ett exempel
         * är för hämtning av options för profiler, som har samma struktor på flera olika typer av profiler
         */
        dataType?: string;
        disabled?: boolean;
        searchable?: boolean;
        /**
         * Om clearable är false visas dropdown-pilen istället för 'x'
         */
        clearable?: boolean;
        /**
         * Parametrar som skickas med requesten till BE. Används tillsammans med url och dataType
         */
        urlParams?: { [key: string]: unknown };
        validation?: string | Array<[rule: string, value?: string | number | boolean | RegExp]>;
        /**
         * Human readable name som ska visas vid felmeddelanden
         */
        validationName?: string;
        /**
         * Ska inmatad text (sökning, innan val av värde) fortsätta visas efter blur?
         */
        keepSearchOnBlur?: boolean;
        /**
         * Ska dropdownen döljas?
         */
        hideDropdown?: boolean;
        /**
         * Minsta antal tecken innan sökning görs
         */
        minQuery?: number;
        optionMapper?: (data: any) => { value: string | number | boolean; label: string; [key: string]: unknown };
        /**
         * KSuffix.vue prop Text som renderas under input
         */
        suffixText?: string;
        /**
         * KSuffix.vue prop Ikon som renderas under input
         */
        suffixIcon?: string;
        /**
         * KSuffix.vue prop Typ av subtitle
         */
        suffixType?: undefined | "none" | "info" | "info-dark";
        converter?: ({ value }: { value: unknown | unknown[] }) => unknown | unknown[]; // Funktion som returnerar förändrade värdet
        value?: unknown;
        /**
         * Ska komponenten registreras med formulate vid mount?
         * Används för att undvika renderingsproblem av komponent som kan försvinna helt från DOM
         */
        registerOnMountId?: string;
        /**
         * Funktion som anropas med vald option och returnerar relevant del av optionen.
         * Default (om inte den här proppen sätts) är att option.value returneras
         */
        reduce?: (option: Option) => unknown;
        /**
         * Dropdown-riktning. Om "up" tvingas den uppåt i alla lägen.
         */
        direction?: "up" | "default";
        /**
         * Sträng av klasser som sätts på FormulateInput containern.
         * Se tillgängliga klasser i scss nedan ("containerClasses").
         */
        containerClasses?: string;
        /**
         * Ska options hämtas vid mount? Gäller om url är satt. Default är true
         */
        retrieveOptionsOnMount?: boolean;
        /**
         * Ska en ny hämtning av options automatiskt göras om urlParams förändras? Default är false. urlParams
         * används bara och triggrar ny hämtning om url är satt. inputOptions OCH defaultOptions sätts vid ny hämtning
         */
        reactiveUrlParams?: boolean;
        /**
         * Ska dropdownen stängas vid val av option?
         */
        closeOnSelect?: boolean;
        /**
         * Storlek på label
         */
        labelSize?: "default" | "large" | "small";
        /**
         * Ersätter allt som renderas för valda options med en räknare som visar antalet valda options
         */
        showSelectedCounter?: boolean;
        /**
         * Text som visas om showSelectedCounter är satt. "Visar 10 av *counterLabel*"
         */
        counterLabel?: string;
        /**
         * Ska option för att välja alla/avvälja alla visas? Gäller vid multiple
         */
        showAllOption?: boolean;
        variant?: "default" | "basic";
        /**
         * Text som kommer visas istället för valda options i sökfältet
         */
        overrideLabel?: string;
        /**
         * Ska positionering av dropdown räknas ut automatiskt? Kan placeras ovanför och under input
         */
        autoPosition?: boolean;
        /**
         * Gör så att om värdet i fältet är tomt (tom sträng) eller falsy
         * så kommer det rensas i formulärdatan vid submit.
         */
        ignoreEmptyData?: boolean;
        /**
         * Custom ID som sätts på Vue select-komponenter. Kan användas för att targetta dropdown med global css.
         * vs__ läggs till i början av namnet och __listbox till slutet
         */
        uid?: string;
        /**
         * Ska fältet vara synligt? Override för inställning av synlighet som görs via config
         */
        shouldBeVisible?: boolean;
        /**
         * Externa felmeddelanden som visas i fältet. Påverkar inte hasValidationErrors() på formulärnivå.
         */
        errors?: string[];
        /**
         * Visa asterisk på label för att markera att fältet är obligatoriskt, utan att lägga till required-validering.
         */
        showAsRequired?: boolean;
        placeholder?: string;
    }

    const emit = defineEmits<{
        /**
         * ANVÄND BARA INPUTVALUE NÄR KAUTOCOMPLETE ANVÄNDS UTANFÖR FORMULÄR.
         * Använd KForm listeners/triggers i andra fall
         */
        (event: "inputValue", payload: { value: unknown | unknown[]; name: string }): void;
        (event: "search:blur"): void;
        (e: "input", value: unknown): void;
        (e: "option:selected", value: unknown): void;
    }>();

    const props = withDefaults(defineProps<Props>(), {
        options: () => [],
        disabled: false,
        searchable: false,
        clearable: true,
        urlParams: () => ({}),
        minQuery: 1,
        value: () => null,
        direction: "default",
        retrieveOptionsOnMount: true,
        reactiveUrlParams: false,
        closeOnSelect: true,
        showAllOption: false,
        variant: "default",
        autoPosition: false,
        ignoreEmptyData: false,
        shouldBeVisible: undefined,
    });

    const {
        options,
        onSearch,
        url,
        dataType,
        disabled,
        urlParams,
        validation,
        name,
        minQuery,
        optionMapper,
        suffixText,
        converter,
        value,
        registerOnMountId,
        reduce,
        retrieveOptionsOnMount,
        reactiveUrlParams,
        counterLabel,
        showSelectedCounter,
        overrideLabel,
        ignoreEmptyData,
        shouldBeVisible,
    } = toRefs(props);

    const inputValue = ref(value.value);
    const inputOptions = ref<Option[]>([]);
    /**
     * Default options, de som visades när komponenten renderades. Kan användas för att ha en förifylld lista med som
     * kan visas igen om användaren gjort ett val efter sökning
     */
    const defaultOptions = ref<Option[]>([]);
    const modifiedDisabled = ref(disabled?.value);
    const selectRef = ref();
    const inputRef = ref();

    const modifiedSuffix = ref(suffixText?.value);
    const internalUrlParams = ref(urlParams?.value);

    const setSuffix = ({ value }: { value: string | undefined }) => {
        modifiedSuffix.value = value;
    };

    watch(
        () => props.suffixText,
        (newValue: any) => {
            modifiedSuffix.value = newValue;
        }
    );

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    } | null>("configNames", null);

    const { modifiedValidation, expose: useValidationExpose } = useValidation({
        name: name.value,
        validation: validation,
        moduleName: configNames?.moduleName,
        subModuleName: configNames?.subModuleName,
    });

    const setDisabled = ({ value }: { value: boolean }) => {
        modifiedDisabled.value = value;
    };

    /**
     * Funktion för att sätta nya options. setDefault true innebär att den också sätter inskickande options till
     * default (som visas när sökfältet töms)
     */
    const setOptions = ({
        options,
        setDefault,
        setSelectedValues,
    }: {
        options: Option[];
        setDefault?: boolean;
        setSelectedValues?: boolean;
    }) => {
        if (setDefault) {
            defaultOptions.value = options;
        }

        inputOptions.value = options;

        if (setSelectedValues) {
            const selectedValues = options.filter((option) => option.selected).map((option) => option.value);

            inputValue.value = selectedValues;
        }
    };

    const setUrlParams = async ({ value }: { value: { [key: string]: unknown } }) => {
        internalUrlParams.value = value;

        if (url?.value && reactiveUrlParams.value) {
            await setOptionsByRequestByDataType();
        }
    };

    defineExpose({
        kType: FormComponentType.Autocomplete,
        setDisabled,
        setOptions,
        convertData: converter?.value,
        getSearchValue: () => selectRef?.value?.search,
        getSelectedOption: () => selectRef?.value?.selectedValue,
        ...useValidationExpose,
        setSuffix,
        setUrlParams,
        ignoreEmptyData: () => ignoreEmptyData.value,
    });

    const internalShouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            ...(configNames ?? {}),
            name: name.value,
            overrideValue: shouldBeVisible?.value,
        })
    );

    /**
     * Label räknas ut baserat på om räknare ska visas eller om overrideLabel är satt
     */
    const customLabel = computed(() => {
        if (overrideLabel?.value && showSelectedCounter?.value && Array.isArray(inputValue?.value)) {
            return `${overrideLabel.value} (${inputValue.value.length})`;
        }

        if (overrideLabel?.value) {
            return overrideLabel.value;
        }

        if (!showSelectedCounter?.value || !Array.isArray(inputValue?.value)) {
            return "";
        }

        return `${inputValue?.value.length === inputOptions.value.length ? "Alla" : inputValue.value.length}${
            counterLabel?.value ? ` ${counterLabel.value} ` : " "
        }valda`;
    });

    /**
     * Stödfunktion för att avgöra om en option är valbar eller inte
     * Används i vue-select selectable prop
     */
    const isSelectable = (option: any) => {
        if (typeof option.disabled == "function") {
            return !option.disabled();
        }

        return !option.disabled;
    };

    /**
     * Stödfunktion för att underlätta hämtning av data som ska manglas på samma sätt och spotta ut en mängd options.
     * Exempel är profiler
     */
    const requestByDataType = async ({
        url,
        dataType,
        query,
        optionMapper,
    }: {
        url: string;
        dataType?: string;
        query?: string;
        optionMapper?: (data: any) => { value: string | number | boolean; label: string; [key: string]: unknown };
    }) => {
        try {
            let responseOptions: Option[] = [];

            // TODO bryt ut else till egen funktion
            // eftersom det faktiskt inte är en "request by data type" och requestByDataType ska pensioneras i framtiden)
            if (dataType === "profiles") {
                responseOptions = await profileOptions(
                    await fetchData({
                        url: url,
                        params: {
                            ...internalUrlParams?.value,
                            q: query,
                        },
                    })
                );
            } else {
                const response = await klinikenApi.get(
                    url,
                    getConfig({
                        params: {
                            ...internalUrlParams?.value,
                            q: query,
                        },
                    })
                );

                const results = response.data.results || response.data;

                if (optionMapper) {
                    responseOptions = results.map(optionMapper);
                } else {
                    responseOptions = results.map((item: any) => ({
                        label: item.displayName,
                        value: item.id,
                        ...item,
                    }));
                }
            }

            return responseOptions;
        } catch (error) {
            openDialog({
                title: "Fel vid hämtning av data",
                message: getErrorMessage(error),
                type: "error",
            });
            return [];
        }
    };

    /**
     * Funktion som anropas vid sökning
     */

    const search = debounce(async (query: string, loading: (isLoading: boolean) => void) => {
        // Blockerar så att search inte körs i autocompletes med statiska options
        if (!onSearch?.value && !url?.value) {
            return;
        }

        loading(true);

        // Om queryn är tom (t.ex efter att användaren valt ett alternativ eller om fältet rensats)
        if (!query) {
            // Filtrera options till det som valts av användaren
            if (inputValue?.value || typeof inputValue?.value === "number") {
                inputOptions.value = inputOptions.value?.filter((option) => option.value === inputValue.value);
                // Sätt options till default om query är tom och inget val gjorts
            } else {
                inputOptions.value = defaultOptions.value;
            }
            // Om url är satt kommer det göras en anrop till den och inte via onSearch-funktionen
        } else if (query.length >= minQuery.value && url?.value) {
            inputOptions.value = await requestByDataType({
                url: url.value,
                dataType: dataType?.value,
                query,
                optionMapper: optionMapper?.value,
            });
            // Anropa onSearch
        } else if (query.length >= minQuery.value && onSearch?.value) {
            const data = await onSearch.value({ query });
            inputOptions.value = optionMapper?.value ? data.map(optionMapper.value) : data;
        }

        loading(false);
    }, 1000);

    /**
     * Funktion som anropas med vald option och returnerar relevant del av optionen.
     * Anropar inskickad funktion (reduce). Annars returnerar den default, vilket är option.value
     */
    const internalReduce = (option: Option) => {
        if (reduce?.value) {
            return reduce.value(option);
        }

        return option.value;
    };

    const setOptionsByRequestByDataType = async () => {
        if (url?.value) {
            inputOptions.value = await requestByDataType({
                url: url.value,
                dataType: dataType?.value,
                optionMapper: optionMapper?.value,
            });
            defaultOptions.value = cloneDeep(inputOptions.value);
        }
    };

    const isSelected = ({ value }: { value: Option["value"] }) => {
        return !!selectRef?.value?.selectedValue?.find?.((selectedOption: Option) => selectedOption.value === value);
    };

    /**
     * Anropas när välj alla/avvälj alla klickas på
     */
    const onAllClick = () => {
        if (!Array.isArray(inputOptions?.value) || !Array.isArray(inputValue?.value as unknown[])) {
            return;
        }

        if (inputOptions.value.length === (inputValue.value as unknown[]).length) {
            inputValue.value = [];

            return;
        }

        inputValue.value = inputOptions.value.map((option: Option) => option.value);
    };

    /**
     * Anropas av Vue select för att räkna ut var dropdown ska renderas. Anropas bara om
     * autoposition: true är satt
     */

    const calculatePosition = (
        dropdownList: HTMLHtmlElement,
        component: {
            $refs: {
                toggle: HTMLHtmlElement;
            };
        }
    ) => {
        return autoUpdate(dropdownList, component.$refs.toggle, () =>
            computePosition(component.$refs.toggle, dropdownList, {
                placement: "bottom-start",
                middleware: [
                    flip({
                        fallbackPlacements: ["bottom-start", "bottom", "bottom-end", "top-start", "top", "top-end"],
                    }),
                ],
            }).then(({ x, y }) => {
                Object.assign(dropdownList.style, {
                    left: `${x}px`,
                    top: `${y}px`,
                });
            })
        );
    };

    const onSearchBlur = () => {
        emit("search:blur");
    };

    // För att hantera att vueselect kan tömmas genom clear och själv inte har ett event hantera vi att
    // använder vi en watcher på inputValue. Är value tomt så sätter vi default options.
    watch(
        inputValue,
        async (newValue) => {
            if (!newValue) {
                inputOptions.value = defaultOptions.value;
            }
        },
        { immediate: true }
    );

    // Uppdaterar options när propen förändras, t.ex. när föräldern hämtar dem async efter mount.
    // Gäller bara statiska options (inte url, som hanterar sina egna options).
    // Jämförelsen görs mot propens föregående värde och inte mot defaultOptions: en förälder som skickar in
    // en array-literal (:options="[]") ger en ny array vid varje omrendering utan att innehållet ändrats, och
    // då ska options som satts via setOptions ligga kvar.
    watch(
        () => props.options,
        (newValue, oldValue) => {
            if (url?.value || !Array.isArray(newValue) || isEqual(newValue, oldValue)) {
                return;
            }

            inputOptions.value = [...newValue];
            defaultOptions.value = [...newValue];
        }
    );

    watch(
        urlParams,
        async (newValue, oldValue) => {
            internalUrlParams.value = newValue;

            if (
                url?.value &&
                reactiveUrlParams.value &&
                newValue !== undefined &&
                !isProxy(newValue) &&
                !isEqual(oldValue, newValue)
            ) {
                await setOptionsByRequestByDataType();
            }
        },
        {
            immediate: true,
        }
    );

    onBeforeMount(async () => {
        if (url?.value) {
            if (retrieveOptionsOnMount.value) {
                await setOptionsByRequestByDataType();
            } else {
                inputOptions.value = [];
                defaultOptions.value = [];
            }
        } else if (options?.value) {
            if (Array.isArray(options.value)) {
                inputOptions.value = [...options.value];
                defaultOptions.value = [...options.value];
            } else if (typeof options.value === "function") {
                try {
                    const result = await options.value();
                    inputOptions.value = optionMapper?.value ? result.map(optionMapper?.value) : result;
                    defaultOptions.value = cloneDeep(result);
                } catch (error) {
                    openDialog({
                        title: "Fel vid hämtning av data",
                        message: getErrorMessage(error),
                        type: "error",
                    });
                }
            }
        }

        // Kollar om det finns några förvalda options inskickade
        const preselectedInputOptions = inputOptions.value?.filter((option: Option) => option.selected) ?? [];

        // Sätter förvalda options
        if (preselectedInputOptions.length) {
            inputValue.value = preselectedInputOptions.map(({ value }) => value);
        }
    });

    onMounted(async () => {
        if (registerOnMountId?.value && inputRef?.value?.$formulate?.registry) {
            /*
                Registrerar komponenten till formulate vid mount
                 Används för att undvika renderingsproblem av komponent som kan försvinna helt från DOM
             */
            await inputRef.value.$formulate.registry
                .get(registerOnMountId.value)
                .registry.ctx.register(name.value, inputRef.value);
        }
    });

    watch(inputValue, (newValue) => {
        if (
            typeof (newValue as Option)?.label === "string" &&
            (newValue as Option)?.value !== undefined &&
            !inputOptions.value.find((option) => option.value === (newValue as Option).value)
        ) {
            inputOptions.value = [newValue as Option, ...inputOptions.value];
        }

        /**
         * ANVÄND BARA DET HÄR NÄR KAUTOCOMPLETE ANVÄNDS UTANFÖR FORMULÄR.
         * Använd KForm listeners/triggers i andra fall
         */
        emit("inputValue", { value: newValue, name: name.value });
    });
</script>

<template>
    <!--
        Vi gör en fuling och använder type select i formulate input för att slippa göra en plugin av KAutocomplete.
        Det gör det lättare att migrera över till FormKit
    -->
    <FormulateInput
        ref="inputRef"
        :class="[
            'k-autocomplete',
            containerClasses ? ` ${containerClasses}` : '',
            `variant-${variant}`,
            {
                multiple,
                'open-indicator-instead-of-clearable': !clearable,
                'custom-search': showSelectedCounter || overrideLabel,
            },
        ]"
        v-if="internalShouldBeVisible"
        :disabled="modifiedDisabled"
        v-model="inputValue"
        @input="emit('input', $event)"
        :name="name"
        type="select"
        :validation="modifiedValidation"
        :validationName="validationName ?? label"
        :errors="errors"
    >
        <template #label>
            <KLabel
                v-if="label"
                :content="label"
                :validation="modifiedValidation"
                :size="labelSize"
                :showAsRequired="showAsRequired"
            />
        </template>
        <template #element="context">
            <!-- Koppling av context.blurHandler och @close görs för att synka state mellan Vueselect och formulate -->
            <!-- search ska bara sättas om det faktiskt ska användas, vilket inte görs om onSearch/url inte är satt -->
            <VueSelect
                ref="selectRef"
                v-model="inputValue"
                :class="[{ 'direction-upwards': direction === 'up' }, `variant-${variant}`]"
                :options="inputOptions"
                :multiple="multiple"
                @search="search"
                @option:selected="emit('option:selected', $event)"
                :reduce="internalReduce"
                :disabled="modifiedDisabled"
                :selectable="isSelectable"
                :clearable="clearable"
                @close="context.blurHandler"
                :clearSearchOnBlur="keepSearchOnBlur ? () => false : undefined"
                :dropdownShouldOpen="hideDropdown ? () => false : undefined"
                :closeOnSelect="multiple ? false : closeOnSelect"
                :deselectFromDropdown="true"
                :appendToBody="autoPosition"
                :calculatePosition="autoPosition ? calculatePosition : undefined"
                :uid="`__${uid}`"
                @search:blur="onSearchBlur"
                :placeholder="placeholder"
            >
                <!-- Valt alternativ -->
                <template #selected-option="{ label, value }">
                    <slot name="selected-option" :option="{ label, value }" />
                </template>
                <!-- Om fältet har fokus och inga options hittats efter sökning eller om fältet är tomt -->
                <template #no-options="{ searching }">
                    <div class="no-options">
                        <span v-if="searching" class="no-option">Det finns inga alternativ</span>
                        <span v-else class="empty-query">Börja skriv för att söka</span>
                    </div>
                </template>
                <!-- Vid sökning -->
                <template #spinner="{ loading }">
                    <div v-if="loading" class="vs__spinner" />
                </template>
                <!-- Om onSearch satts antar vi att sökikonen ska visas istället för standard chevron -->
                <template v-if="onSearch || url" #open-indicator>
                    <img :src="SearchIcon" alt="Sök" class="search-indicator" />
                </template>
                <template #option="option">
                    <slot
                        name="option"
                        :option="option"
                        :selected="isSelected({ value: option.value })"
                        :multiple="multiple"
                    >
                        <KAutocompleteOption
                            :content="option.label"
                            :selected="isSelected({ value: option.value })"
                            :multiple="multiple"
                        />
                    </slot>
                </template>
                <!-- Renderas högst upp i dropdown -->
                <template #list-header>
                    <slot name="list-header" />
                    <KAutocompleteOption
                        v-if="multiple && showAllOption"
                        multiple
                        :onClick="onAllClick"
                        class="list-header-option"
                        :content="
                            inputOptions?.length &&
                            Array.isArray(inputValue) &&
                            inputValue?.length &&
                            inputOptions.length === inputValue.length
                                ? 'Avvälj alla'
                                : 'Välj alla'
                        "
                        :selected="
                            !!(
                                inputOptions?.length &&
                                Array.isArray(inputValue) &&
                                inputValue?.length &&
                                inputOptions.length === inputValue.length
                            )
                        "
                    />
                </template>
                <template #list-footer>
                    <div v-if="$slots['list-footer']" class="list-footer">
                        <div class="divider" />
                        <div class="footer-content">
                            <slot name="list-footer" />
                        </div>
                    </div>
                </template>
                <template #selected-option-container>
                    <!-- Ser till att alla valda options döljs för att kunna renderas custom content -->
                    <template v-if="showSelectedCounter || overrideLabel">
                        <div style="display: none" />
                    </template>
                </template>
                <!-- Rendering av allt i sökfältet -->
                <template #search="{ attributes, events }">
                    <!-- Custom input för att kunna styra vad som ska renderas -->
                    <template v-if="showSelectedCounter || overrideLabel">
                        <input
                            class="vs__search custom-search"
                            v-bind="attributes"
                            v-on:compositionstart="events.compositionstart"
                            v-on:compositionend="events.compositionend"
                            v-on:keydown="events.keydown"
                            v-on:blur="events.blur"
                            v-on:focus="events.focus"
                            v-on:input="events.input"
                            :placeholder="customLabel"
                        />
                        <div class="custom-chevron">
                            <img src="@/assets/arrow_down.svg" alt="Expandera" />
                        </div>
                    </template>
                </template>
            </VueSelect>
        </template>
        <template #help>
            <KSuffix class="span-all" :subText="modifiedSuffix" :subIcon="suffixIcon" :subType="suffixType" />
        </template>
    </FormulateInput>
</template>

<style lang="scss">
    // Utanför scope för att kunna targetta dropdown som renderas i body när autoposition true är satt
    body {
        > .vs__dropdown-menu {
            padding-left: 0;

            .vs__dropdown-option,
            .list-header-option {
                padding: 0.3rem 0.8rem;
            }

            &:has(.list-footer) {
                padding-bottom: 0;
            }

            .list-footer {
                position: sticky;
                bottom: 0;
                padding: 0.3rem 0.8rem;
                background-color: inherit;

                .divider {
                    width: calc(100% + 0.4rem);
                    margin-left: -0.2rem;
                    height: 1px;
                    background-color: #d3d8da;
                    margin-bottom: 0.3rem;
                }

                .footer-content {
                    padding: 0.3rem 0;
                }
            }
        }
    }
</style>

<style scoped lang="scss">
    @import "@/style/form";
    @import "@/style/variables";

    .k-autocomplete {
        $padding-action: 1rem;

        &.open-indicator-instead-of-clearable {
            ::v-deep {
                .vs__actions {
                    padding-inline: $padding-action !important;
                }
                .vs__open-indicator {
                    display: block !important;
                }
            }
        }

        /* Prop: containerClasses */
        &.no-margin-block {
            margin-block: 0 !important;
        }

        ::v-deep .formulate-input-wrapper {
            input {
                border: none;
            }

            &:has(input:focus) {
                .vs__dropdown-toggle {
                    border: $input-border-focus;
                }

                input:focus {
                    outline: 0;
                    border: none;
                    margin: 0;
                }
            }

            .no-options {
                padding: 0.1rem 0.2rem;

                // Tomt fokuserat sökfält
                .empty-query {
                    opacity: 0.5;
                }
            }

            // Om fältet är disabled, det visade någon background färg på v-search fält, vilket gör att man inte kan se texten i disablat läge
            .vs--disabled .vs__search {
                background-color: transparent;
            }

            .vs__selected-options {
                width: 100%;
                min-height: fit-content;
                height: 100%;

                input::placeholder {
                    color: $color-text-grey;
                }
            }
            // TODO Ta bort när autoPosition true är default
            .direction-upwards .vs__dropdown-menu {
                top: unset;
                bottom: 100%;
            }
            .vs__dropdown-toggle {
                min-height: fit-content;
                height: calc($form-input-height-default + $input-border-width * 2);
                padding: unset;

                .vs__actions {
                    width: unset;
                    padding: 0;

                    // Rensaknappen
                    .vs__clear {
                        margin-right: unset;
                        padding: 0.5rem;
                        padding-right: $padding-action;
                    }

                    // Laddningsikonen
                    .vs__spinner {
                        border-left-color: rgba(88, 151, 251, 0.71);
                    }
                }
            }

            // Om data laddas in
            .vs--loading {
                .vs__open-indicator,
                .vs__clear,
                .search-indicator {
                    display: none;
                }

                .vs__actions {
                    padding-right: calc($padding-action / 2);
                }
            }

            .vs--searching {
                .vs__search {
                    opacity: 1;
                }

                /*
                Av någon anledning sätts opacity på sökfältet till 0.2 när dropdownShouldOpen är false och overridar
                den här cssen, så sätter den till !important
                */
                &:not(.vs--open):not(.vs--loading) .vs__search {
                    opacity: 1 !important;
                }
            }

            // TODO Ta bort när autoPosition true är default
            .vs__dropdown-menu {
                padding-left: 0;

                .vs__dropdown-option,
                .list-header-option {
                    padding: 0.3rem 0.8rem;
                }
            }

            .vs__dropdown-toggle:has(.custom-search) {
                .custom-search {
                    width: 100%;
                    padding-right: calc(16px + $icon-gap-default);
                }

                .vs__actions {
                    width: 30px;

                    .vs__open-indicator {
                        display: unset;
                    }
                }

                .custom-chevron {
                    position: absolute;
                    right: 0;

                    img {
                        width: 16px;
                        height: auto;
                    }
                }
            }

            .vs__dropdown-toggle:has(.custom-search:focus) {
                background-color: blue;
            }
        }

        &.variant-basic {
            margin-top: 0;
            margin-bottom: 0;

            ::v-deep .formulate-input-wrapper {
                .v-select {
                    &.variant-basic {
                        .vs__dropdown-toggle {
                            height: unset;
                            border: none;
                            background: none !important;
                        }

                        input::placeholder {
                            color: $form-label-default-color;
                        }
                    }

                    .vs__dropdown-toggle:has(.custom-search) {
                        .vs__selected-options {
                            width: auto;
                            margin-bottom: 0;
                        }

                        .vs__actions {
                            display: none;
                        }

                        .custom-search {
                        }
                    }
                }
            }
        }

        // Om värde valts
        &[data-has-value="true"] {
            ::v-deep .formulate-input-wrapper {
                // Collapse/expand eller search-ikon
                .vs__open-indicator,
                .search-indicator {
                    display: none;
                }
            }
        }

        &:not([data-has-value="true"]) {
            ::v-deep .formulate-input-wrapper {
                // Om data inte laddas in
                .v-select:not(.vs--loading) {
                    // Collapse/expand eller search-ikon
                    .vs__actions {
                        padding-right: $padding-action;
                    }
                }
            }
        }

        &[data-has-errors="true"] {
            &[data-is-showing-errors="true"]:not(:has(.vs--open)) {
                ::v-deep div[role="combobox"] {
                    border-bottom: $input-border-error;
                }
            }
        }

        &.multiple {
            ::v-deep .formulate-input-wrapper {
                .vs__selected-options {
                    margin-bottom: 3px;
                }

                .vs__selected {
                    background-color: unset;
                }
            }
        }
    }
</style>
